import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import styles from './Simulation.module.css';
import { useAppContext } from '@/context/AppContext';
import dayjs from 'dayjs';
import { formatTimeAMPM } from '@/utils/helpers';

// Import icons
// Import icons as components
import * as Icons from 'react-icons/fa';

// Import Google Maps components
import { GoogleMap, useJsApiLoader, Marker, InfoWindow, Polyline } from '@react-google-maps/api';

interface LocationData {
  point: {
    coordinates?: [number, number]; // [longitude, latitude]
  };
  timestamp: string;
  source?: string;
}

interface ReplayState {
  isPlaying: boolean;
  currentTime: number; // Unix timestamp in seconds
  speed: number; // Replay speed multiplier
  sourceEnabled: { [key: string]: boolean }; // Track which sources are enabled
}

const SimulationPage: React.FC = () => {
  const navigate = useNavigate();
  const { getSourceBackendUrl, getDestBackendUrl, apiKey, theme } = useAppContext();
  const [locationData, setLocationData] = useState<LocationData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [vehicleInfo, setVehicleInfo] = useState<any>(null);
  const [vehicleId, setVehicleId] = useState<string>('');
  const [selectedSources, setSelectedSources] = useState<string>('');
  
  // Replay state
  const [replayState, setReplayState] = useState<ReplayState>({
    isPlaying: false,
    currentTime: 0,
    speed: 1,
    sourceEnabled: {}
  });
  
  // Store sorted data by source
  const [sortedDataBySource, setSortedDataBySource] = useState<{[key: string]: LocationData[]}>({});
  
  // References for replay
  const replayTimerRef = useRef<number | null>(null);
  const currentTimeRef = useRef<HTMLDivElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  
  // WebSocket references
  const cityfloWsRef = useRef<WebSocket | null>(null);
  // Track WebSocket connection attempts to prevent duplicates
  const cityfloWsConnectingRef = useRef<boolean>(false);
  const [wsStatus, setWsStatus] = useState<{[key: string]: string}>({});
  
  // Map state
  const [selectedMarker, setSelectedMarker] = useState<LocationData | null>(null);
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number } | null>(null);
  const [currentPoints, setCurrentPoints] = useState<LocationData[]>([]);
  
  // Load Google Maps API
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: 'AIzaSyCY_Gq0uidHzLnNB72dexu1Y-kt3KJOf7M'
  });
  
  // No need to update map options when theme changes - CSS handles it
  // We'll keep the theme state for other components that might need it

  useEffect(() => {
    // Check if we have the required data in localStorage
    const storedVehicleInfo = localStorage.getItem('simulation_vehicle_info');
    const storedVehicleId = localStorage.getItem('simulation_vehicle_id');
    const storedSources = localStorage.getItem('simulation_sources');
    
    // If any required data is missing, redirect to home
    if (!storedVehicleInfo || !storedVehicleId || !storedSources || !apiKey) {
      console.warn('Missing simulation data, redirecting to home');
      navigate('/');
      return;
    }
    
    // Define an interface for the vehicle info structure
    interface VehicleInfoData {
      vehicle_id: string;
      bus_number: string;
      ride_date: string;
      start_time: string;
      end_time: string;
      driver_token: string;
      [key: string]: any; // Allow for additional properties
    }
    
    // Set the data from localStorage
    let parsedVehicleInfo: VehicleInfoData;
    try {
      parsedVehicleInfo = JSON.parse(storedVehicleInfo) as VehicleInfoData;
      setVehicleInfo(parsedVehicleInfo);
      // Use the original vehicle_ride_id from localStorage for navigation
      setVehicleId(localStorage.getItem('simulation_vehicle_ride_id') || '');
      setSelectedSources(storedSources);
    } catch (e) {
      console.error('Failed to parse stored simulation data:', e);
      navigate('/');
      return;
    }
    
    // Verify that we have a valid vehicle_id
    if (!parsedVehicleInfo || !parsedVehicleInfo.vehicle_id) {
      console.error('Missing vehicle_id in stored vehicle info');
      navigate('/');
      return;
    }
    
    // Fetch location data
    const fetchData = async () => {
      try {
        const backendUrl = getSourceBackendUrl();
        
        // Debug vehicle ride data
        console.log('Vehicle ride data:', parsedVehicleInfo);
        
        // Convert ride date and times to Unix timestamps
        // Add one day to end time to ensure we capture all data
        const startTimestamp = getUnixTimestamp(parsedVehicleInfo.ride_date, parsedVehicleInfo.start_time);
        let endTimestamp = getUnixTimestamp(parsedVehicleInfo.ride_date, parsedVehicleInfo.end_time);
        
        // If timestamps couldn't be generated, use fallback values (24-hour period)
        if (!startTimestamp || !endTimestamp) {
          console.warn('Using fallback timestamps due to parsing issues');
          const now = Math.floor(Date.now() / 1000);
          const oneDayInSeconds = 24 * 60 * 60;
          return navigate('/');
        }
        
        // Fetch location data with timestamps
        const locationDataUrl = `${backendUrl}/api/tools/tracking-sim/fetch-location-data/?vehicle_id=${parsedVehicleInfo.vehicle_id}&sources=${storedSources}&start_ts=${startTimestamp}&end_ts=${endTimestamp}`;
        const locationResponse = await fetch(locationDataUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-Tracking-Sim-API-Key': apiKey
          },
          mode: 'cors'
        });

        if (!locationResponse.ok) {
          throw new Error(`Error fetching location data: ${locationResponse.statusText}`);
        }

        const locationResult = await locationResponse.json();
        const locationData = locationResult.location_data || [];
        setLocationData(locationData);
        
        // Process the location data for replay
        if (locationData.length > 0) {
          console.log(`Processing ${locationData.length} location points for replay`);
          processLocationData(locationData);
        } else {
          console.warn('No location data received from API');
        }
        
        setLoading(false);
      } catch (err: any) {
        console.error('Error fetching data:', err);
        setError(err.message || 'Failed to fetch data');
        setLoading(false);
      }
    };

    fetchData();
  }, [apiKey, getSourceBackendUrl, navigate]);

  // Format date for display using dayjs
  const formatDate = (dateString: string) => {
    try {
      // Check if date is in DD-MM-YYYY format
      if (dateString.match(/^\d{2}-\d{2}-\d{4}$/)) {
        const [day, month, year] = dateString.split('-').map(Number);
        return dayjs(`${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`)
          .format('MMMM D, YYYY');
      } else {
        // Fallback to original format
        return dayjs(dateString).format('MMMM D, YYYY');
      }
    } catch (e) {
      return dateString || 'N/A';
    }
  };
  
  // Format time in HH:MM AM/PM format using dayjs
  const formatTimeHHMM = (timeString: string) => {
    try {
      return dayjs(timeString).format('h:mm A');
    } catch (e) {
      return 'N/A';
    }
  };
  
  // Convert timestamp to Unix timestamp (seconds)
  const toUnixTimestamp = (timestamp: string): number => {
    return dayjs(timestamp).unix();
  };
  
  // Format Unix timestamp (seconds) to readable date/time
  const formatUnixTimestamp = (unixTimestamp: number, format: string = 'YYYY-MM-DD HH:mm:ss'): string => {
    return dayjs.unix(unixTimestamp).format(format);
  };

  // Helper function to extract latitude from a location point
  const getLatitude = (point: LocationData): number | null => {
    if (point.point?.coordinates && Array.isArray(point.point.coordinates) && point.point.coordinates.length >= 2) {
      // Format is [longitude, latitude]
      return point.point.coordinates[1];
    }
    return null;
  };
  
  // Helper function to extract longitude from a location point
  const getLongitude = (point: LocationData): number | null => {
    if (point.point?.coordinates && Array.isArray(point.point.coordinates) && point.point.coordinates.length >= 2) {
      // Format is [longitude, latitude]
      return point.point.coordinates[0];
    }
    return null;
  };
  
  // Get source name from ID
  const getSourceName = (sourceId: string) => {
    switch (sourceId) {
      case '1': return 'Cityflo';
      case '2': return 'Fleetx';
      case '3': return 'Intangles';
      case '4': return 'Cautio';
      default: return 'Unknown';
    }
  };
  
  // Get source color based on source ID - match the CSS source colors
  const getSourceColor = (sourceId: string): string => {
    switch (sourceId) {
      case '1': return '#4CAF50'; // Cityflo - Green
      case '2': return '#2196F3'; // Fleetx - Blue
      case '3': return '#FF9800'; // Intangles - Orange
      case '4': return '#9C27B0'; // Cautio - Purple
      default: return '#9AA0A6'; // Gray
    }
  };

    // Get source color based on source ID - match the CSS source colors
    const getSourcePolylineWeight = (sourceId: string): number => {
      switch (sourceId) {
        case '1': return 5; // Cityflo - Green
        case '2': return 3; // Fleetx - Blue
        case '3': return 3; // Intangles - Orange
        case '4': return 3; // Cautio - Purple
        default: return 3; // Gray
      }
    };
  
  // Get statistics for each source
  interface SourceStat {
    id: string;
    name: string;
    count: number;
    percentage: number;
    firstTimestamp: string | null;
    lastTimestamp: string | null;
  }
  
  const getSourceStatistics = (): SourceStat[] => {
    if (!locationData.length || !selectedSources) return [];
    
    const sourceIds = selectedSources.split(',');
    const stats = sourceIds.map(id => {
      const sourceData = locationData.filter(point => point.source === id);
      
      // Get first and last timestamps if data exists
      let firstTimestamp = null;
      let lastTimestamp = null;
      
      if (sourceData.length > 0) {
        // Sort data by timestamp to ensure correct order
        const sortedData = [...sourceData].sort((a, b) => 
          dayjs(a.timestamp).valueOf() - dayjs(b.timestamp).valueOf()
        );
        firstTimestamp = sortedData[0].timestamp;
        lastTimestamp = sortedData[sortedData.length - 1].timestamp;
      }
      
      return {
        id,
        name: getSourceName(id),
        count: sourceData.length,
        percentage: Math.round((sourceData.length / locationData.length) * 100),
        firstTimestamp,
        lastTimestamp
      };
    });
    
    return stats;
  };
  
  // Process location data for replay
  const processLocationData = (data: LocationData[]) => {
    console.log('Processing location data for replay:', data.length, 'points');
    if (!data.length) {
      console.warn('No data to process');
      return;
    }
    
    // Sort all data by timestamp
    const sortedData = [...data].sort((a, b) => 
      dayjs(a.timestamp).valueOf() - dayjs(b.timestamp).valueOf()
    );
    
    // Get global start time
    const startTimestamp = dayjs(sortedData[0].timestamp).unix();
    console.log('Start timestamp:', startTimestamp, '=', formatUnixTimestamp(startTimestamp));
    
    // Group data by source
    const dataBySource: {[key: string]: LocationData[]} = {};
    const sourceEnabledState: {[key: string]: boolean} = {};
    
    // Initialize sources from the data points themselves
    const uniqueSources = new Set<string>();
    sortedData.forEach(point => {
      if (point.source) {
        uniqueSources.add(point.source);
      }
    });
    
    console.log('Unique sources found in data:', Array.from(uniqueSources));
    
    // Initialize all detected sources
    uniqueSources.forEach(source => {
      dataBySource[source] = [];
      sourceEnabledState[source] = true; // Enable all sources by default
    });
    
    // Populate with data
    sortedData.forEach(point => {
      if (point.source) {
        dataBySource[point.source].push(point);
      }
    });
    
    // Log the data by source
    Object.entries(dataBySource).forEach(([source, points]) => {
      console.log(`Source ${source}: ${points.length} points`);
    });
    
    // Set initial replay state with proper source enabled flags
    setReplayState(prev => ({
      ...prev,
      currentTime: startTimestamp,
      sourceEnabled: sourceEnabledState
    }));
    
    // Store sorted data
    setSortedDataBySource(dataBySource);
    console.log('Sorted data by source:', Object.keys(dataBySource).map(k => `${k}: ${dataBySource[k].length} points`));
    
    // Center map on the first location point
    const firstPoint = sortedData[0];
    const lat = getLatitude(firstPoint);
    const lng = getLongitude(firstPoint);
    
    if (lat && lng) {
      setMapCenter({ lat, lng });
      console.log('Centering map on first location:', { lat, lng });
    }
    
    // Data is processed, but we'll rely on the useEffect hook to set up WebSockets
    // to avoid duplicate connection attempts
    // setupWebSockets(); // Removed to prevent duplicate connections
  };
  
  // Replay control functions
  const togglePlay = () => {
    setReplayState(prev => {
      const newIsPlaying = !prev.isPlaying;
      return {
        ...prev,
        isPlaying: newIsPlaying
      };
    });
  };
  
  // Watch for isPlaying state changes to start/stop replay
  useEffect(() => {
    if (replayState.isPlaying) {
      console.log('Play state changed to true, starting replay sequence');
      startReplaySequence();
    } else {
      console.log('Play state changed to false, stopping replay sequence');
      stopReplaySequence();
    }
  }, [replayState.isPlaying]);
  
  // Watch for currentTime changes to update the display and map center
  useEffect(() => {
    console.log('Current time updated to:', replayState.currentTime, '=', new Date(replayState.currentTime * 1000).toLocaleString());
    
    // Find the latest point across all enabled sources at the current time
    const findLatestPoint = () => {
      let latestPoint: LocationData | null = null;
      let latestTimestamp = 0;
      
      // Iterate through all sources
      Object.entries(sortedDataBySource).forEach(([sourceId, sourceData]) => {
        // Skip if source is disabled
        if (!replayState.sourceEnabled[sourceId]) return;
        
        // Find the latest point from this source that's before or at current time
        const pointsUpToNow = sourceData.filter(point => 
          dayjs(point.timestamp).unix() <= replayState.currentTime
        );
        
        if (pointsUpToNow.length > 0) {
          const lastPoint = pointsUpToNow[pointsUpToNow.length - 1];
          const timestamp = dayjs(lastPoint.timestamp).unix();
          
          // Update if this is the latest point we've found
          if (timestamp > latestTimestamp) {
            latestTimestamp = timestamp;
            latestPoint = lastPoint;
          }
        }
      });
      
      return latestPoint;
    };
    
    // Update map center to follow the latest point
    const latestPoint = findLatestPoint();
    if (latestPoint) {
      const lat = getLatitude(latestPoint);
      const lng = getLongitude(latestPoint);
      
      if (lat && lng) {
        setMapCenter({ lat, lng });
        console.log('Updating map center to follow latest point:', { lat, lng });
      }
    }
  }, [replayState.currentTime, sortedDataBySource, replayState.sourceEnabled]);
  
  // Start the replay sequence
  const startReplaySequence = () => {
    console.log('Starting replay sequence');
    console.log('Current sortedDataBySource:', Object.keys(sortedDataBySource).map(k => `${k}: ${sortedDataBySource[k].length} points`));
    
    // Flatten and sort all location data by timestamp
    const allData = Object.values(sortedDataBySource).flat();
    console.log('All data length:', allData.length);
    
    if (!allData.length) {
      console.error('No location data available to replay');
      // If we have location data but sortedDataBySource is empty, try processing it again
      if (locationData.length > 0) {
        console.log('Attempting to reprocess location data...');
        processLocationData(locationData);
        return;
      }
      return;
    }
    
    console.log(`Found ${allData.length} total location points to replay`);
    
    const sortedAllData = [...allData].sort((a, b) => 
      new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );
    
    // Find the current index based on the current time
    const currentTime = replayState.currentTime;
    let startIndex = 0;
    
    for (let i = 0; i < sortedAllData.length; i++) {
      const pointTime = dayjs(sortedAllData[i].timestamp).unix();
      if (pointTime >= currentTime) {
        startIndex = i;
        break;
      }
    }
    
    console.log(`Starting replay from index ${startIndex} (time: ${dayjs(sortedAllData[startIndex].timestamp).format('h:mm:ss A')})`);
    
    // Make sure all sources are properly initialized in the sourceEnabled state
    const updatedSourceEnabled = { ...replayState.sourceEnabled };
    let sourcesChanged = false;
    
    // Initialize any missing sources
    sortedAllData.forEach(point => {
      if (point.source && updatedSourceEnabled[point.source] === undefined) {
        updatedSourceEnabled[point.source] = true; // Enable by default
        sourcesChanged = true;
      }
    });
    
    // If we had to update the sources, update the state first
    if (sourcesChanged) {
      console.log('Updating source enabled state:', updatedSourceEnabled);
      setReplayState(prev => ({
        ...prev,
        sourceEnabled: updatedSourceEnabled
      }));
    }
    
    // Start the replay sequence from the current index
    processNextLocationUpdate(sortedAllData, startIndex);
  };
  
  // Process the next location update in sequence
  const processNextLocationUpdate = (sortedData: LocationData[], currentIndex: number) => {
    // Stop if we've reached the end or replay is stopped
    if (currentIndex >= sortedData.length) {
      console.log('Replay sequence ended - reached end of data');
      setReplayState(prev => ({ ...prev, isPlaying: false }));
      return;
    }
    
    // Check if replay is still playing (this could have changed since we scheduled this function)
    if (!replayState.isPlaying) {
      console.log('Replay sequence stopped - play state is false');
      return;
    }
    
    const currentPoint = sortedData[currentIndex];
    const currentTime = dayjs(currentPoint.timestamp).unix();
    
    console.log(`Processing point ${currentIndex+1}/${sortedData.length} at time ${dayjs(currentPoint.timestamp).format('h:mm:ss A')}`);
    
    // Validate the point has valid coordinates
    let hasValidCoordinates = false;
    
    if (currentPoint.point) {
      if (currentPoint.point.coordinates && Array.isArray(currentPoint.point.coordinates) && currentPoint.point.coordinates.length >= 2) {
        // Coordinates array format is valid
        hasValidCoordinates = true;
      }
    }
    
    if (!hasValidCoordinates) {
      console.warn(`Point at index ${currentIndex} has invalid coordinates:`, JSON.stringify(currentPoint));
      // Skip to next point
      const timeoutId = window.setTimeout(() => {
        processNextLocationUpdate(sortedData, currentIndex + 1);
      }, 100);
      replayTimerRef.current = timeoutId;
      return;
    }
    
    // Update the current time display
    setReplayState(prev => ({
      ...prev,
      currentTime: currentTime
    }));
    
    // Process the current point if its source is enabled
    const sourceId = currentPoint.source;
    if (sourceId && replayState.sourceEnabled[sourceId]) {
      // Extract lat/lng for logging
      let lat, lng;
      if (currentPoint.point.coordinates && Array.isArray(currentPoint.point.coordinates)) {
        lng = currentPoint.point.coordinates[0];
        lat = currentPoint.point.coordinates[1];
      }
      
      console.log(`Source ${sourceId} is enabled, sending update for point:`, {
        lat: lat,
        lng: lng,
        timestamp: currentPoint.timestamp
      });
      sendLocationUpdates(sourceId, [currentPoint]);
    } else {
      console.log(`Source ${sourceId || 'unknown'} is disabled or undefined, skipping update`);
    }
    
    // Schedule the next update based on the time difference
    if (currentIndex < sortedData.length - 1) {
      const nextPoint = sortedData[currentIndex + 1];
      const nextTime = dayjs(nextPoint.timestamp).unix();
      const timeDiff = (nextTime - currentTime) * 1000 / replayState.speed; // Convert to milliseconds and apply speed
      
      console.log(`Scheduling next update in ${timeDiff}ms`);
      
      // Use a minimum delay of 10ms to prevent UI freezing
      const delay = Math.max(10, timeDiff);
      
      replayTimerRef.current = window.setTimeout(() => {
        processNextLocationUpdate(sortedData, currentIndex + 1);
      }, delay);
    } else {
      // We've reached the end
      stopReplaySequence();
      setReplayState(prev => ({
        ...prev,
        isPlaying: false
      }));
    }
  };
  
  // Stop the replay sequence
  const stopReplaySequence = () => {
    console.log('Stopping replay sequence');
    if (replayTimerRef.current) {
      console.log('Clearing timeout:', replayTimerRef.current);
      window.clearTimeout(replayTimerRef.current);
      replayTimerRef.current = null;
    } else {
      console.log('No active timeout to clear');
    }
    
    // We don't close WebSockets when stopping, only when disabling a source
    // This allows for quicker resumption of replay
  };
  
  // Clean up WebSockets on unmount
  useEffect(() => {
    return () => {
      // Close Cityflo WebSocket
      if (cityfloWsRef.current) {
        cityfloWsRef.current.close();
        cityfloWsRef.current = null;
      }
    };
  }, []);
  
  // Send location updates for a specific source
  const sendLocationUpdates = (sourceId: string, points: LocationData[]) => {
    // Only send updates if the source is enabled
    if (!replayState.sourceEnabled[sourceId]) {
      console.log(`Source ${sourceId} is disabled, skipping updates`);
      return;
    }
    
    const sourceName = sourceId === '1' ? 'Cityflo' : 
                      sourceId === '2' ? 'Fleetx' : 
                      sourceId === '3' ? 'Intangles' : 
                      sourceId === '4' ? 'Cautio' : 'Unknown';
    
    console.log(`Sending ${points.length} updates for source ${sourceName}`);
    
    // Source-specific handlers
    switch (sourceId) {
      case '1': // Cityflo
        sendCityfloUpdates(points);
        break;
      case '2': // Fleetx
        sendFleetxUpdates(points);
        break;
      case '3': // Intangles
        sendIntanglesUpdates(points);
        break;
      case '4': // Cautio
        sendCautioUpdates(points);
        break;
      default:
        console.warn(`Unknown source ID: ${sourceId}`);
    }
  };
  
  // Setup WebSocket connections
  const setupWebSockets = () => {
    if (!vehicleInfo) {
      console.warn('Vehicle info not available, cannot setup WebSockets');
      return;
    }
    
    console.log('Setting up WebSockets for vehicle:', vehicleInfo.id);
    
    // Setup Cityflo WebSocket regardless of enabled state
    // We'll connect early to ensure it's ready when needed
    setupCityfloWebSocket();
  };
  
  // Effect to setup WebSockets when vehicle info is available
  // This is the primary place where WebSockets should be initialized
  useEffect(() => {
    if (vehicleInfo && !loading && !error) {
      console.log('Vehicle info loaded, setting up WebSockets');
      setupWebSockets();
    }
    
    // Cleanup function to reset connection state and close connections
    return () => {
      if (cityfloWsRef.current) {
        console.log('Cleaning up WebSocket connections on component unmount');
        cityfloWsRef.current.close();
        cityfloWsRef.current = null;
      }
      cityfloWsConnectingRef.current = false;
    };
  }, [vehicleInfo, loading, error]);
  
  // Setup Cityflo WebSocket
  const setupCityfloWebSocket = () => {
    // Prevent duplicate connection attempts
    if (cityfloWsConnectingRef.current) {
      console.log('WebSocket connection already in progress, skipping duplicate attempt');
      return;
    }
    
    // Close existing connection if any
    if (cityfloWsRef.current) {
      console.log('Closing existing WebSocket connection');
      cityfloWsRef.current.close();
      cityfloWsRef.current = null;
    }
    
    // Set connecting flag to prevent duplicate attempts
    cityfloWsConnectingRef.current = true;
    
    // Get the driver token from vehicle info
    const driverToken = vehicleInfo.driver_token;
    if (!driverToken) {
      console.error('Missing driver token for Cityflo WebSocket');
      setWsStatus(prev => ({ ...prev, cityflo: 'Error: Missing driver token' }));
      return;
    }
    
    // Determine WebSocket URL based on environment
    const backendUrl = getDestBackendUrl();
    // Use ws:// for localhost, wss:// for production
    const isLocalhost = backendUrl.includes('localhost') || backendUrl.includes('127.0.0.1');
    const protocol = isLocalhost ? 'ws://' : 'wss://';
    const wsUrl = `${protocol}${backendUrl.replace(/^https?:\/\//, '')}/api/ws/driver-location/?token=${driverToken}`;
    
    console.log('Connecting to WebSocket URL:', wsUrl);
    
    try {
      // Create WebSocket connection
      const ws = new WebSocket(wsUrl);
      
      // Setup event handlers
      ws.onopen = () => {
        console.log('Cityflo WebSocket connected successfully');
        setWsStatus(prev => ({ ...prev, cityflo: 'Connected' }));
      };
      
      ws.onmessage = (event) => {
        console.log('Received WebSocket message:', event.data);
      };
      
      ws.onclose = (event) => {
        console.log('Cityflo WebSocket disconnected', event.code, event.reason);
        setWsStatus(prev => ({ ...prev, cityflo: 'Disconnected' }));
      };
      
      ws.onerror = (error) => {
        console.error('Cityflo WebSocket error:', error);
        setWsStatus(prev => ({ ...prev, cityflo: 'Error: Connection failed' }));
      };
      
      // Store the WebSocket reference
      cityfloWsRef.current = ws;
    } catch (error) {
      console.error('Failed to setup Cityflo WebSocket:', error);
      setWsStatus(prev => ({ ...prev, cityflo: 'Error: Setup failed' }));
    } finally {
      // Reset connecting flag regardless of success or failure
      setTimeout(() => {
        cityfloWsConnectingRef.current = false;
      }, 1000); // Add a small delay to prevent rapid reconnection attempts
    }
  };
  
  // Source-specific update handlers
  const sendCityfloUpdates = (points: LocationData[]) => {
    // Check if WebSocket is connected
    if (!cityfloWsRef.current) {
      console.warn('Cityflo WebSocket not initialized, attempting to connect...');
      setupCityfloWebSocket();
      return;
    }
    
    if (cityfloWsRef.current.readyState !== WebSocket.OPEN) {
      console.warn(`Cityflo WebSocket not open, current state: ${cityfloWsRef.current.readyState}`);
      console.warn('WebSocket states: CONNECTING=0, OPEN=1, CLOSING=2, CLOSED=3');
      
      // If it's closed, try to reconnect
      if (cityfloWsRef.current.readyState === WebSocket.CLOSED) {
        console.warn('WebSocket is CLOSED, attempting to reconnect...');
        setupCityfloWebSocket();
      }
      return;
    }
    
    console.log(`Preparing to send ${points.length} Cityflo updates via WebSocket`);
    
    // Send location updates through WebSocket
    let sentCount = 0;
    points.forEach(point => {
      try {
        // Log the raw point data to debug
        console.log('Raw point data:', JSON.stringify(point));
        
        // Extract lat/lng from coordinates array or direct lat/lng properties
        let lat, lng;
        
        if (point.point?.coordinates && Array.isArray(point.point.coordinates) && point.point.coordinates.length >= 2) {
          // Format is [longitude, latitude]
          lng = point.point.coordinates[0];
          lat = point.point.coordinates[1];
          console.log('Extracted lat/lng from coordinates array:', { lat, lng });
        } else {
          console.error('Invalid point format, cannot extract lat/lng:', point);
          return; // Skip this point
        }
        
        // Format the payload according to the required structure
        // Use current timestamp instead of historical timestamp
        const payload: {
          lat: number;
          lng: number;
          loc_ac_av: boolean;
          loc_ac: number;
          ts: number;
        } = {
          lat: lat,
          lng: lng,
          loc_ac_av: true,
          loc_ac: 10,
          ts: dayjs().unix() // Current timestamp
        };
        
        console.log('Using current timestamp instead of historical:', {
          historical: dayjs(point.timestamp).unix(),
          current: payload.ts
        });
        
        // Log the payload we're about to send
        console.log('Formatted payload:', payload);
        
        // Send the payload
        const message = JSON.stringify(payload);
        cityfloWsRef.current!.send(message);
        console.log(`Sent Cityflo update ${++sentCount}/${points.length}:`, payload);
      } catch (error) {
        console.error('Failed to send Cityflo update:', error);
      }
    });
    
    console.log(`Completed sending ${sentCount}/${points.length} Cityflo updates`);
  };
  
  const sendFleetxUpdates = (points: LocationData[]) => {
    // Implementation for Fleetx updates
    console.log('Fleetx updates:', points);
  };
  
  const sendIntanglesUpdates = (points: LocationData[]) => {
    // Implementation for Intangles updates
    console.log('Intangles updates:', points);
  };
  
  const sendCautioUpdates = async (points: LocationData[]) => {
    if (!vehicleInfo || !vehicleInfo.license_plate) {
      console.error('Vehicle license plate not available, cannot send Cautio updates');
      return;
    }
    
    console.log(`Preparing to send ${points.length} Cautio updates via REST API`);
    
    // Get the destination backend URL
    const backendUrl = getDestBackendUrl();
    const cautioEndpoint = `${backendUrl}/api/location/cautio/`;
    
    // Send location updates through REST API
    let sentCount = 0;
    let failedCount = 0;
    
    for (const point of points) {
      try {
        // Extract lat/lng from coordinates array
        let lat, lng;
        
        if (point.point?.coordinates && Array.isArray(point.point.coordinates) && point.point.coordinates.length >= 2) {
          // Format is [longitude, latitude]
          lng = point.point.coordinates[0];
          lat = point.point.coordinates[1];
          console.log('Extracted lat/lng from coordinates array:', { lat, lng });
        } else {
          console.error('Invalid point format, cannot extract lat/lng:', point);
          failedCount++;
          continue; // Skip this point
        }
        
        // Format the payload according to the required structure
        const payload = {
          lat: lat,
          lng: lng,
          acc: 6.0,
          vehicle_id: vehicleInfo.license_plate,
          time: Math.floor(Date.now()) // Current timestamp in millis
        };
        
        console.log('Sending Cautio update:', payload);
        
        // Send the payload to the Cautio endpoint
        const response = await fetch(cautioEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Tracking-Sim-Api-Key': apiKey
          },
          body: JSON.stringify(payload)
        });
        
        if (response.ok) {
          console.log(`Sent Cautio update ${++sentCount}/${points.length}`);
        } else {
          const errorData = await response.json();
          console.error('Failed to send Cautio update:', errorData);
          failedCount++;
        }
        
        // Add a small delay between requests to avoid overwhelming the server
        await new Promise(resolve => setTimeout(resolve, 100));
        
      } catch (error) {
        console.error('Error sending Cautio update:', error);
        failedCount++;
      }
    }
    
    console.log(`Completed sending Cautio updates: ${sentCount} sent, ${failedCount} failed`);
    setWsStatus(prev => ({ ...prev, cautio: sentCount > 0 ? 'Updates Sent' : 'Failed' }));
  };
  
  // Time control functions
  const jumpTime = (seconds: number) => {
    // First, stop the current replay sequence
    const wasPlaying = replayState.isPlaying;
    if (wasPlaying) {
      console.log('Stopping replay before time jump');
      stopReplaySequence();
    }
    
    // Update the current time
    setReplayState(prev => {
      // Get the time bounds
      const allData = Object.values(sortedDataBySource).flat();
      if (!allData.length) return prev;
      
      const sortedAllData = [...allData].sort((a, b) => 
        dayjs(a.timestamp).valueOf() - dayjs(b.timestamp).valueOf()
      );
      
      const firstTimestamp = dayjs(sortedAllData[0].timestamp).unix();
      const lastTimestamp = dayjs(sortedAllData[sortedAllData.length - 1].timestamp).unix();
      
      // Calculate new time and ensure it's within bounds
      let newTime = prev.currentTime + seconds;
      newTime = Math.max(firstTimestamp, Math.min(lastTimestamp, newTime));
      
      console.log(`Jumping time from ${prev.currentTime} to ${newTime} (${seconds} seconds)`);
      
      // Return new state with updated time and paused playback
      return {
        ...prev,
        currentTime: newTime,
        isPlaying: false // Always pause when jumping
      };
    });
    
    // Use setTimeout to ensure the state is updated before restarting
    if (wasPlaying) {
      setTimeout(() => {
        console.log('Restarting replay after time jump');
        setReplayState(prev => ({
          ...prev,
          isPlaying: true
        }));
      }, 100);
    }
  };
  
  // Toggle source enabled/disabled
  const toggleSource = (sourceId: string) => {
    console.log(`Toggling source ${sourceId}`);
    
    setReplayState(prev => {
      const newEnabled = !prev.sourceEnabled[sourceId];
      console.log(`Source ${sourceId} will be ${newEnabled ? 'enabled' : 'disabled'}`);
      
      const newState = {
        ...prev,
        sourceEnabled: {
          ...prev.sourceEnabled,
          [sourceId]: newEnabled
        }
      };
      
      // If we're enabling Cityflo, set up the WebSocket
      if (sourceId === '1' && newEnabled) {
        console.log('Enabling Cityflo source, setting up WebSocket');
        setupCityfloWebSocket();
      }
      
      // If we're disabling Cityflo and it's connected, close the WebSocket
      if (sourceId === '1' && !newEnabled && cityfloWsRef.current) {
        console.log('Disabling Cityflo source, closing WebSocket');
        cityfloWsRef.current.close();
        cityfloWsRef.current = null;
        setWsStatus(prev => ({ ...prev, cityflo: 'Disabled' }));
      }
      
      return newState;
    });
  };
  
  // Format current replay time using dayjs
  const formatReplayTime = () => {
    if (replayState.currentTime === 0) return 'Not started';
    
    return dayjs.unix(replayState.currentTime).format('h:mm:ss A');
  };
  
  // Calculate progress percentage for the progress bar
  const calculateProgressPercentage = () => {
    // Get all data points
    const allData = Object.values(sortedDataBySource).flat();
    if (!allData.length) return 0;
    
    // Sort by timestamp using dayjs
    const sortedAllData = [...allData].sort((a, b) => 
      dayjs(a.timestamp).valueOf() - dayjs(b.timestamp).valueOf()
    );
    
    // Get first and last timestamps using dayjs
    const firstTimestamp = dayjs(sortedAllData[0].timestamp).unix();
    const lastTimestamp = dayjs(sortedAllData[sortedAllData.length - 1].timestamp).unix();
    
    // Calculate progress percentage
    const totalDuration = lastTimestamp - firstTimestamp;
    if (totalDuration <= 0) return 0;
    
    const currentProgress = replayState.currentTime - firstTimestamp;
    const percentage = (currentProgress / totalDuration) * 100;
    
    // Ensure percentage is between 0 and 100
    return Math.max(0, Math.min(100, percentage));
  };
  
  // Handle click on progress bar to jump to specific position
  const handleProgressBarClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!progressBarRef.current) return;
    
    // Get all data points
    const allData = Object.values(sortedDataBySource).flat();
    if (!allData.length) return;
    
    // Sort by timestamp using dayjs
    const sortedAllData = [...allData].sort((a, b) => 
      dayjs(a.timestamp).valueOf() - dayjs(b.timestamp).valueOf()
    );
    
    // Get first and last timestamps using dayjs
    const firstTimestamp = dayjs(sortedAllData[0].timestamp).unix();
    const lastTimestamp = dayjs(sortedAllData[sortedAllData.length - 1].timestamp).unix();
    
    // Calculate total duration
    const totalDuration = lastTimestamp - firstTimestamp;
    if (totalDuration <= 0) return;
    
    // Calculate click position as percentage
    const rect = progressBarRef.current.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const clickPercentage = (clickX / rect.width) * 100;
    
    // Convert percentage to timestamp
    const newTime = firstTimestamp + (totalDuration * clickPercentage / 100);
    
    console.log(`Progress bar clicked at ${clickPercentage.toFixed(2)}%, jumping to time: ${dayjs.unix(newTime).format('YYYY-MM-DD HH:mm:ss')}`);
    
    // Calculate time difference to jump
    const timeDifference = newTime - replayState.currentTime;
    
    // Use jumpTime function to update the time
    jumpTime(timeDifference);
  };
  
  // Convert date and time strings to Unix timestamp (seconds) using dayjs
  const getUnixTimestamp = (dateString: string, timeString: string): number | null => {
    try {
      console.log('Parsing date and time:', { dateString, timeString });
      
      if (!dateString || !timeString) {
        console.error('Missing date or time string');
        return null;
      }
      
      // Parse the date parts - format is DD-MM-YYYY (as confirmed by backend)
      const dateParts = dateString.split('-');
      if (dateParts.length !== 3) {
        console.error('Invalid date format:', dateString);
        return null;
      }
      
      // Parse time parts - format is HH:MM:SS (as confirmed by backend)
      const timeParts = timeString.split(':');
      if (timeParts.length < 2) {
        console.error('Invalid time format:', timeString);
        return null;
      }
      
      // Extract date components (DD-MM-YYYY)
      const day = parseInt(dateParts[0]);
      const month = parseInt(dateParts[1]);
      const year = parseInt(dateParts[2]);
      
      // Extract time components (HH:MM:SS)
      const hour = parseInt(timeParts[0]);
      const minute = parseInt(timeParts[1]);
      const second = timeParts[2] ? parseInt(timeParts[2]) : 0;
      
      // Format date string in YYYY-MM-DD HH:mm:ss format for dayjs
      const formattedDateString = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')} ${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}:${second.toString().padStart(2, '0')}`;
      
      // Create dayjs object and validate
      const date = dayjs(formattedDateString);
      
      if (!date.isValid()) {
        console.error('Invalid date object created:', { year, month, day, hour, minute, second });
        return null;
      }
      
      // Convert to Unix timestamp (seconds)
      const timestamp = date.unix();
      console.log('Generated timestamp:', timestamp, 'for date:', date.format('YYYY-MM-DD HH:mm:ss'));
      return timestamp;
    } catch (e) {
      console.error('Error converting to timestamp:', e);
      return null;
    }
  };

  // Get list of sources being used
  const getSourcesList = () => {
    if (!selectedSources) return [];
    return selectedSources.split(',').map((s: string) => ({
      id: s,
      name: getSourceName(s)
    }));
  };

  return (
    <>
      <Helmet>
        <title>Simulation | Cityflo Tracking Simulator</title>
      </Helmet>
      <div className={styles.container}>
        <div className={styles.content}>
          <Link to={`/vehicle-ride/${vehicleId}`} className={styles.backButton}>
            <svg className={styles.backIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            Back to Ride Details
          </Link>

          {loading ? (
            <div className={styles.loadingContainer}>
              <div className={styles.loadingSpinner}></div>
              <p className={styles.loadingText}>Loading simulation data...</p>
            </div>
          ) : error ? (
            <div className={styles.errorContainer}>
              <h2 className={styles.errorTitle}>Error</h2>
              <p className={styles.errorMessage}>{error}</p>
              <Link to="/" className={styles.errorButton}>
                Go Home
              </Link>
            </div>
          ) : (
            <>
              <div className={styles.header}>
                <h1 className={styles.title}>Replaying {vehicleInfo.bus_number} {formatTimeAMPM(vehicleInfo.start_time)} - {formatTimeAMPM(vehicleInfo.end_time)}</h1>
                {vehicleInfo && (
                  <div className={styles.vehicleInfo}>
                    <div className={styles.infoItem}>
                      <span className={styles.infoLabel}>Vehicle ID</span>
                      <span className={styles.infoValue}>{vehicleInfo.vehicle_id}</span>
                    </div>
                    <div className={styles.infoItem}>
                      <span className={styles.infoLabel}>Date</span>
                      <span className={styles.infoValue}>{formatDate(vehicleInfo.ride_date)}</span>
                    </div>
                  </div>
                )}
                
                {/* Progress Bar */}
                <div 
                  className={styles.progressBarContainer}
                  onClick={handleProgressBarClick}
                  title="Click to jump to a specific position"
                  ref={progressBarRef}
                >
                  <div 
                    className={styles.progressBar} 
                    style={{
                      width: calculateProgressPercentage() + '%'
                    }}
                  ></div>
                </div>
              </div>

              <div className={styles.simulationContainer}>
                <div className={styles.sidebar}>
                  <div className={styles.controlsCard}>
                    <h2 className={styles.cardTitle}>Replay Controls</h2>
                    
                    <div className={styles.currentTimeDisplay} ref={currentTimeRef}>
                      <span className={styles.timeLabel}>Current Time:</span>
                      <span className={styles.timeValue}>{formatReplayTime()}</span>
                    </div>
                    
                    <div className={styles.controls}>
                      <button 
                        className={styles.controlButton} 
                        onClick={() => jumpTime(-300)} 
                        title="Back 5 minutes"
                        disabled={loading || !!error}
                      >
                        {Icons.FaFastBackward({})}
                      </button>
                      <button 
                        className={styles.controlButton} 
                        onClick={() => jumpTime(-60)} 
                        title="Back 1 minute"
                        disabled={loading || !!error}
                      >
                        {Icons.FaStepBackward({})}
                      </button>
                      <button 
                        className={`${styles.controlButton} ${styles.playButton}`} 
                        onClick={togglePlay}
                        title={replayState.isPlaying ? "Pause" : "Play"}
                        disabled={loading || !!error}
                      >
                        {replayState.isPlaying ? Icons.FaPause({}) : Icons.FaPlay({})}
                      </button>
                      <button 
                        className={styles.controlButton} 
                        onClick={() => jumpTime(60)} 
                        title="Forward 1 minute"
                        disabled={loading || !!error}
                      >
                        {Icons.FaStepForward({})}
                      </button>
                      <button 
                        className={styles.controlButton} 
                        onClick={() => jumpTime(300)} 
                        title="Forward 5 minutes"
                        disabled={loading || !!error}
                      >
                        {Icons.FaFastForward({})}
                      </button>
                    </div>
                    
                    <h3 className={styles.sourceToggleTitle}>Source Toggles</h3>
                    <div className={styles.sourceToggles}>
                      {getSourcesList().map((source: { id: string; name: string }) => (
                        <div key={source.id} className={styles.sourceToggle}>
                          <span className={`${styles.sourceIndicator} ${styles[`source${source.id}`]}`}></span>
                          <span className={styles.sourceToggleName}>{source.name}</span>
                          <div className={styles.sourceToggleControls}>
                            {source.id === '1' && wsStatus.cityflo && (
                              <span className={`${styles.wsStatus} ${wsStatus.cityflo.startsWith('Error') ? styles.wsError : wsStatus.cityflo === 'Connected' ? styles.wsConnected : styles.wsDisconnected}`}>
                                {wsStatus.cityflo === 'Connected' ? '●' : wsStatus.cityflo === 'Disconnected' ? '○' : '!'}
                              </span>
                            )}
                            <button 
                              className={styles.toggleButton} 
                              onClick={() => toggleSource(source.id)}
                              title={replayState.sourceEnabled[source.id] ? "Disable source" : "Enable source"}
                              disabled={loading || !!error}
                            >
                              {replayState.sourceEnabled[source.id] ? Icons.FaToggleOn({}) : Icons.FaToggleOff({})}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className={styles.card}>
                    <h2 className={styles.cardTitle}>Location Data</h2>
                    <div className={styles.dataStats}>
                      <div className={styles.statItem}>
                        <span className={styles.statLabel}>Total Points</span>
                        <span className={styles.statValue}>{locationData.length}</span>
                      </div>
                    </div>
                    
                    <h3 className={styles.sourceStatsTitle}>Source Statistics</h3>
                    <div className={styles.sourceStatsGrid}>
                      {getSourceStatistics().map(sourceStat => (
                        <div key={sourceStat.id} className={styles.sourceStatCard}>
                          <div className={styles.sourceStatHeader}>
                            <span className={`${styles.sourceIndicator} ${styles[`source${sourceStat.id}`]}`}></span>
                            <span className={styles.sourceStatName}>{sourceStat.name}</span>
                          </div>
                          <div className={styles.sourceStatContent}>
                            <div className={styles.sourceStatItem}>
                              <span className={styles.sourceStatLabel}>Points</span>
                              <span className={styles.sourceStatValue}>{sourceStat.count}</span>
                            </div>
                            {sourceStat.count > 0 && (
                              <>
                                <div className={styles.sourceStatItem}>
                                  <span className={styles.sourceStatLabel}>Coverage</span>
                                  <span className={styles.sourceStatValue}>{sourceStat.percentage}%</span>
                                </div>
                                <div className={styles.sourceStatItem}>
                                  <span className={styles.sourceStatLabel}>Time Range</span>
                                </div>
                                <div className={styles.sourceTimeRange}>
                                  {formatTimeHHMM(sourceStat.firstTimestamp!)} - {formatTimeHHMM(sourceStat.lastTimestamp!)}
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                
                <div className={styles.mapContainer}>
                  {locationData.length > 0 ? (
                    isLoaded ? (
                      <>
                        <GoogleMap
                          mapContainerClassName={styles.googleMap}
                          center={mapCenter || { lat: 0, lng: 0 }}
                          zoom={15}
                          options={{
                            // Minimize map elements for better performance
                            styles: [
                              // Hide points of interest
                              {
                                "featureType": "poi",
                                "stylers": [{"visibility": "off"}]
                              },
                              // Hide transit stations and lines
                              {
                                "featureType": "transit",
                                "stylers": [{"visibility": "off"}]
                              },
                              // Simplify road labels
                              {
                                "featureType": "road",
                                "elementType": "labels.icon",
                                "stylers": [{"visibility": "off"}]
                              },
                              // Hide business points
                              {
                                "featureType": "poi.business",
                                "stylers": [{"visibility": "off"}]
                              },
                              // Simplify administrative areas
                              {
                                "featureType": "administrative.neighborhood",
                                "stylers": [{"visibility": "off"}]
                              },
                              // Hide landscape features
                              {
                                "featureType": "landscape.man_made",
                                "stylers": [{"visibility": "off"}]
                              }
                            ],
                            fullscreenControl: false,
                            mapTypeControl: false,
                            streetViewControl: false,
                            zoomControl: true,
                            maxZoom: 20,
                            minZoom: 3,
                            // Disable additional controls
                            rotateControl: false,
                            scaleControl: false,
                            scrollwheel: true,
                            disableDefaultUI: true,
                            clickableIcons: false
                          }}
                          onLoad={(map) => {
                            mapRef.current = map;
                            console.log(`Map loaded - theme will be handled by CSS`);
                          }}
                        >
                          {/* Render current points as markers */}
                          {currentPoints.map((point, index) => {
                            const lat = getLatitude(point);
                            const lng = getLongitude(point);
                            
                            if (!lat || !lng) return null;
                            
                            // Get color based on source
                            const sourceId = point.source || '1';
                            const sourceColor = getSourceColor(sourceId);
                            
                            return (
                              <Marker
                                key={`${sourceId}-${index}`}
                                position={{ lat, lng }}
                                onClick={() => setSelectedMarker(point)}
                                icon={{
                                  path: google.maps.SymbolPath.CIRCLE,
                                  fillColor: sourceColor,
                                  fillOpacity: 1,
                                  strokeColor: '#ffffff',
                                  strokeWeight: 2,
                                  scale: 8
                                }}
                              />
                            );
                          })}
                          
                          {/* Show info window for selected marker */}
                          {selectedMarker && (
                            <InfoWindow
                              position={{
                                lat: getLatitude(selectedMarker) || 0,
                                lng: getLongitude(selectedMarker) || 0
                              }}
                              onCloseClick={() => setSelectedMarker(null)}
                            >
                              <div>
                                <p><strong>Source:</strong> {getSourceName(selectedMarker.source || '1')}</p>
                                <p><strong>Time:</strong> {dayjs(selectedMarker.timestamp).format('h:mm:ss A')}</p>
                                <p><strong>Position:</strong> {getLatitude(selectedMarker)?.toFixed(6)}, {getLongitude(selectedMarker)?.toFixed(6)}</p>
                              </div>
                            </InfoWindow>
                          )}
                          
                          {/* Draw polylines for each source */}
                          {Object.keys(sortedDataBySource).map(sourceId => {
                            if (!replayState.sourceEnabled[sourceId]) return null;
                            
                            const sourceData = sortedDataBySource[sourceId] || [];
                            if (sourceData.length < 2) return null;
                            
                            // Filter points up to current time using dayjs
                            // This ensures that rewinding properly updates the polylines
                            // by only showing points up to the current playback time
                            const filteredPoints = sourceData.filter(point => 
                              dayjs(point.timestamp).unix() <= replayState.currentTime
                            );
                            
                            if (filteredPoints.length < 2) return null;
                            
                            // Create path for polyline
                            const path = filteredPoints.map(point => ({
                              lat: getLatitude(point) || 0,
                              lng: getLongitude(point) || 0
                            }));
                            
                            return (
                              <Polyline
                                key={`polyline-${sourceId}`}
                                path={path}
                                options={{
                                  strokeColor: getSourceColor(sourceId),
                                  strokeOpacity: 1,
                                  strokeWeight: getSourcePolylineWeight(sourceId)
                                }}
                              />
                            );
                          })}
                        </GoogleMap>
                        
                        {/* Map Legend */}
                        <div className={styles.mapControls}>
                          <div className={styles.mapLegend}>
                            {getSourcesList().map(source => (
                              <div key={source.id} className={styles.mapLegendItem}>
                                <div 
                                  className={styles.mapLegendColor} 
                                  style={{ backgroundColor: getSourceColor(source.id) }}
                                ></div>
                                <span>{source.name}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className={styles.mapPlaceholder}>
                        <p>Loading Google Maps...</p>
                      </div>
                    )
                  ) : (
                    <div className={styles.mapPlaceholder}>
                      <p>No location data available for this vehicle</p>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default SimulationPage;
